import { GQL_ID } from '@@partner:types:graphql'

import React, { useMemo, useState, useCallback } from 'react'
import { ArrowBack } from 'react-ionicons'
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom'

import HomePageEditor from '@components/marketing/mainPageConfig/form/MainPageConfigEditor'
import { Col, Grid } from 'rsuite'
import BannerGalleryEditor from '@components/marketing/mainPageConfig/editorItem/BannerGalleryEditor'
import MenuEditor from '@components/marketing/mainPageConfig/editorItem/MenuEditor'

import { useRedirectTo } from '@@partner:helpers/hooks/location'

import { ROUTE_MAIN_PAGE_CONFIGS, ROUTE_MAIN_PAGE_CONFIGS_EDIT } from '@@partner:config/routes'
import { CUSTOMER_FRONTEND_URL } from '@@partner:config'
import { MainPageConfigType } from '@@partner:types:app'
import { MAIN_PAGE_CONFIG_TYPE_OPTIONS } from '@components/marketing/mainPageConfig/constants'

import './MainPageConfigFormPage.scss'

export type ActiveEditorState = {
  type: 'bannerGallery'
  bannerTypeCode: string
  bannerTypeName?: string
} | {
  type: 'menu'
  menuId: GQL_ID
  menuCode: string
  menuName?: string
} | null

export default function MainPageConfigFormPage () {
  const history = useHistory()
  const { brandSlug, mainPageConfigId } = useParams<any>()
  const location = useLocation()

  const [ iframRevision, setIframeRevision ] = useState(1)
  const [ activeEditor, setActiveEditor ] = useState<ActiveEditorState>(null)

  const handleCloseEditor = useCallback(() => {
    setActiveEditor(null)
  }, [])

  const initialType = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const typeParam = params.get('type') as MainPageConfigType | null
    if (!typeParam) {
      return undefined
    }

    return MAIN_PAGE_CONFIG_TYPE_OPTIONS.some(({ value }) => value === typeParam)
      ? typeParam
      : undefined
  }, [ location.search ])

  const previewUrl = useMemo(() => {
    if (!mainPageConfigId) return null
    return `${CUSTOMER_FRONTEND_URL}/?id=${mainPageConfigId}&type=${initialType || ''}&revision=${iframRevision}`
  }, [ mainPageConfigId, initialType, iframRevision ])
  
  const redirectToAllConfigs = useRedirectTo(ROUTE_MAIN_PAGE_CONFIGS, {
    brandSlug
  })
  const redirectToConfig = (id: GQL_ID | null) => {
    if (id) {
      history.replace(generatePath(ROUTE_MAIN_PAGE_CONFIGS_EDIT, {
        brandSlug,
        mainPageConfigId: id
      }))
    }
  }

  const handleRefreshIframe = useCallback(() => {
    setIframeRevision((state) => state + 1)
  }, [])

  return (
    <Grid fluid className="MainPageConfigFormPage">
      <Col
        xs={24} 
        className="MainPageConfigFormPage__Container"
      >
        <Col 
          xs={24}
          sm={24}
          md={24}
          lg={12}
          xl={8}
          className="MainPageConfigFormPage__LeftContainer"
        >
          <div className='MainPageConfigFormPage__LeftContainer__Header'>
            <ArrowBack
              style={{ cursor: 'pointer' }}
              onClick={redirectToAllConfigs}
            />
            <h1 className="u-heading">{ mainPageConfigId ? `Настройки главной страницы #${mainPageConfigId}` : 'Добавить настройку главной' }</h1>
          </div>
          
          <HomePageEditor
            selectedConfigId={mainPageConfigId}
            onSelectConfigId={redirectToConfig}
            initialType={initialType}
            onOpenEditor={setActiveEditor}
            onSave={handleRefreshIframe}
          />
        </Col>
        <Col 
          xs={24}
          sm={24}
          md={24}
          lg={12}
          xl={16}
          className="MainPageConfigFormPage__RightContainer"
        >
          {activeEditor ? (
            <>
              <div className="MainPageConfigFormPage__PreviewHeader">
                <span>Редактирование</span>
              </div>
              <div className="MainPageConfigFormPage__EditorContainer">
                {activeEditor.type === 'bannerGallery' && (
                  <BannerGalleryEditor
                    bannerTypeCode={activeEditor.bannerTypeCode}
                    bannerTypeName={activeEditor.bannerTypeName}
                    onClose={handleCloseEditor}
                  />
                )}
                {activeEditor.type === 'menu' && (
                  <MenuEditor
                    menuId={activeEditor.menuId}
                    menuCode={activeEditor.menuCode}
                    menuName={activeEditor.menuName}
                    onClose={handleCloseEditor}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="MainPageConfigFormPage__PreviewHeader">
                <span>Предпросмотр</span>
                {previewUrl && (
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                    Открыть в новой вкладке
                  </a>
                )}
              </div>
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="MainPageConfigFormPage__PreviewFrame"
                  title="Предпросмотр главной страницы"
                />
              ) : (
                <div className="MainPageConfigFormPage__PreviewEmpty">
                  Сохраните настройку для предпросмотра
                </div>
              )}
            </>
          )}
        </Col>
      </Col>
    </Grid>
  )
}